import React, { useEffect, useMemo, useState } from 'react';
import Footer from '../components/Footer';
import {
  adminApiService,
  AnalyticsSummary,
  ControlPanelSettings,
} from '../services/admin-api';

type PanelSection = 'overview' | 'ai' | 'models' | 'branding' | 'analytics' | 'system';

const SECTION_LABELS: Record<PanelSection, string> = {
  overview: 'Overview',
  ai: 'AI Settings',
  models: 'Models & Fallbacks',
  branding: 'Branding',
  analytics: 'Analytics',
  system: 'System',
};

const ControlPanelPage: React.FC = () => {
  const [selectedSection, setSelectedSection] = useState<PanelSection>('overview');
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState<ControlPanelSettings | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);

  const sections = useMemo(() => Object.keys(SECTION_LABELS) as PanelSection[], []);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      try {
        const session = await adminApiService.getSession();
        if (!isMounted) {
          return;
        }

        if (!session.authenticated) {
          setAuthenticated(false);
          return;
        }

        setAuthenticated(true);
        const [nextSettings, nextAnalytics] = await Promise.all([
          adminApiService.getSettings(),
          adminApiService.getAnalyticsSummary(),
        ]);

        if (isMounted) {
          setSettings(nextSettings);
          setAnalytics(nextAnalytics);
        }
      } catch {
        if (isMounted) {
          setError('Control panel is unavailable. Verify admin API deployment.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    try {
      const session = await adminApiService.login(password);
      if (!session.authenticated) {
        setError('Login failed.');
        return;
      }

      setAuthenticated(true);
      setPassword('');

      const [nextSettings, nextAnalytics] = await Promise.all([
        adminApiService.getSettings(),
        adminApiService.getAnalyticsSummary(),
      ]);
      setSettings(nextSettings);
      setAnalytics(nextAnalytics);
    } catch {
      setError('Login failed. Check admin backend/session configuration.');
    }
  };

  const handleLogout = async () => {
    await adminApiService.logout();
    setAuthenticated(false);
    setSettings(null);
    setAnalytics(null);
    setSelectedSection('overview');
  };

  const renderSection = () => {
    if (!settings) {
      return <p className="text-gray-400">No settings loaded.</p>;
    }

    switch (selectedSection) {
      case 'overview':
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-300">Founder-only operating console for AI controls and analytics.</p>
            <p className="text-sm text-gray-400">Last update: {new Date(settings.updatedAt).toLocaleString()}</p>
            <ul className="text-sm text-gray-300 list-disc pl-6 space-y-1">
              <li>AI enabled: {settings.ai.enabled ? 'Yes' : 'No'}</li>
              <li>Primary model: {settings.ai.primaryModel}</li>
              <li>Fallback count: {settings.ai.fallbackModels.length}</li>
              <li>OpenRouter key configured: {settings.secrets.openRouterConfigured ? 'Yes' : 'No'}</li>
            </ul>
          </div>
        );
      case 'ai':
        return (
          <div className="space-y-3 text-sm text-gray-300">
            <p>Assistant enabled: {settings.ai.enabled ? 'On' : 'Off'}</p>
            <p>Tone preset: {settings.ai.tonePreset}</p>
            <p>Grounding strictness: {settings.ai.groundingStrictness}</p>
            <p>Temperature: {settings.ai.temperature}</p>
            <p>Max tokens: {settings.ai.maxTokens}</p>
          </div>
        );
      case 'models':
        return (
          <div className="space-y-3 text-sm text-gray-300">
            <p>Primary model: {settings.ai.primaryModel}</p>
            <p>Fallback order:</p>
            <ol className="list-decimal pl-6 space-y-1">
              {settings.ai.fallbackModels.map((model) => (
                <li key={model}>{model}</li>
              ))}
            </ol>
          </div>
        );
      case 'branding':
        return (
          <div className="space-y-3 text-sm text-gray-300">
            <p>Logo mode: {settings.branding.logoMode}</p>
            <p>Wordmark: {settings.branding.wordmark}</p>
            <p>Logo source: {settings.branding.logoUrl || 'Managed externally (e.g., DatoCMS)'}</p>
          </div>
        );
      case 'analytics':
        return (
          <div className="space-y-3 text-sm text-gray-300">
            <p>Conversations: {analytics?.conversations ?? 0}</p>
            <p>Fallback rate: {analytics?.fallbackRate ?? 0}%</p>
            <p>Error rate: {analytics?.errorRate ?? 0}%</p>
            <p>Common questions:</p>
            <ul className="list-disc pl-6 space-y-1">
              {(analytics?.topQuestions || []).map((question) => (
                <li key={question}>{question}</li>
              ))}
              {(!analytics || analytics.topQuestions.length === 0) && <li>No data yet</li>}
            </ul>
          </div>
        );
      case 'system':
        return (
          <div className="space-y-3 text-sm text-gray-300">
            <p>Admin API base: {process.env.REACT_APP_ADMIN_API_BASE_URL || '/api/admin'}</p>
            <p>OpenRouter key status: {settings.secrets.openRouterConfigured ? `Configured (${settings.secrets.openRouterLast4 || '****'})` : 'Not configured'}</p>
            <p className="text-gray-400">Secrets are never returned raw to this UI.</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <p>Loading control panel...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <form className="w-full max-w-md bg-gray-900 rounded-xl border border-gray-800 p-6 space-y-4" onSubmit={handleLogin}>
            <h1 className="text-xl font-semibold">Founder Control Panel</h1>
            <p className="text-sm text-gray-400">Admin authentication is required.</p>
            <label className="block">
              <span className="text-sm text-gray-300">Admin password</span>
              <input
                type="password"
                className="mt-2 w-full rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-netflix-red"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              className="w-full bg-netflix-red text-white font-semibold py-2 rounded-lg hover:bg-red-600 transition"
            >
              Sign in
            </button>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      <div className="flex flex-1">
        <aside className="w-64 border-r border-gray-800 p-4">
          <h1 className="text-lg font-semibold mb-4">Control Panel</h1>
          <nav className="space-y-2">
            {sections.map((section) => (
              <button
                key={section}
                type="button"
                onClick={() => setSelectedSection(section)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                  selectedSection === section ? 'bg-gray-800 text-white' : 'text-gray-300 hover:bg-gray-900'
                }`}
              >
                {SECTION_LABELS[section]}
              </button>
            ))}
          </nav>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-6 w-full text-left px-3 py-2 rounded-md text-sm text-red-300 hover:bg-gray-900"
          >
            Sign out
          </button>
        </aside>

        <main className="flex-1 p-6">
          <header className="mb-6">
            <h2 className="text-2xl font-semibold">{SECTION_LABELS[selectedSection]}</h2>
            <p className="text-sm text-gray-400 mt-1">Internal founder-only controls. Not linked in public navigation.</p>
          </header>

          <section className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            {renderSection()}
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ControlPanelPage;
